import {
  apiV2AreaTreePost,
  apiV2CircuitListPost,
  apiV2DeviceFlowMeterDeviceListPost,
  apiV2EnergyResilienceConfigGasDropdownPost,
  apiV2EnergyResilienceConfigGasOutletGetGasSourcePost,
} from '@maxtropy/device-customer-apis-v2';
import { MediumType, ScopeKey, SelectOption } from '../types';
import {
  toAreaTreeOptions,
  toCircuitOptions,
  toDeviceNameOptions,
  toDeviceOptions,
  toGasSourceTreeOptions,
  toNameOptions,
} from './mappers';
import { FetchOptionsParams } from './types';

export const DEVICE_TYPE_ID = {
  AirCompressor: 5114,
  FlowMeter: 5035,
} as const;

const getSettledValue = <T>(result: PromiseSettledResult<T>) =>
  result.status === 'fulfilled' ? result.value : undefined;

export const fetchConfigOptions = async ({
  activeEnergy,
  currentDimension,
  currentMediumType,
  currentScope,
  isFactory,
}: FetchOptionsParams): Promise<Record<string, SelectOption[]>> => {
  if (activeEnergy === MediumType.Electricity) {
    return {};
  }

  const [sourceResult, nameResult] = await Promise.allSettled([
    apiV2EnergyResilienceConfigGasOutletGetGasSourcePost({
      mediumType: currentMediumType,
      dimension: currentDimension,
    } as any),
    isFactory
      ? Promise.resolve(undefined)
      : apiV2EnergyResilienceConfigGasDropdownPost({ mediumType: currentMediumType, dimension: currentDimension }),
  ]);
  const sourceRes = getSettledValue(sourceResult);
  const nameRes = getSettledValue(nameResult);
  const gasSources: SelectOption[] = (sourceRes?.list ?? []).map(item => ({
    label: item.zoneName ?? '',
    value: item.zoneName ?? '',
  }));
  const nameOptions = toNameOptions(nameRes?.list);

  return {
    gasSources,
    ...(currentScope === ScopeKey.Station ? { stationName: nameOptions } : {}),
    ...(currentScope === ScopeKey.Area ? { areaName: nameOptions } : {}),
    ...(currentScope === ScopeKey.Outlet ? { outletName: nameOptions } : {}),
    ...(currentScope === ScopeKey.Compressor ? { compressorName: nameOptions } : {}),
  };
};

export const fetchModalOptions = async ({
  activeEnergy,
  currentScope,
}: Pick<FetchOptionsParams, 'activeEnergy' | 'currentScope'>): Promise<Record<string, SelectOption[]>> => {
  if (activeEnergy === MediumType.Electricity) {
    const [circuitResult, areaTreeResult] = await Promise.allSettled([
      apiV2CircuitListPost({ mediumType: MediumType.Electricity }),
      apiV2AreaTreePost({}),
    ]);
    const circuitRes = getSettledValue(circuitResult);
    const areaTreeRes = getSettledValue(areaTreeResult);

    return {
      circuitKey: toCircuitOptions(circuitRes?.list?.filter(i => !i.used)),
      areaIds: toGasSourceTreeOptions(areaTreeRes?.list ?? []),
    };
  }

  const [flowMeterResult, compressorResult, areaTreeResult] = await Promise.allSettled([
    apiV2DeviceFlowMeterDeviceListPost({
      typeId: DEVICE_TYPE_ID.FlowMeter,
    }),
    currentScope === ScopeKey.Compressor
      ? apiV2DeviceFlowMeterDeviceListPost({
          typeId: DEVICE_TYPE_ID.AirCompressor,
          mediumType: activeEnergy,
          dimension: currentScope as any,
        })
      : Promise.resolve(undefined),
    apiV2AreaTreePost({ mediumType: activeEnergy, dimension: currentScope as any }),
  ]);
  const flowMeterRes = getSettledValue(flowMeterResult);
  const compressorRes = getSettledValue(compressorResult);
  const areaTreeRes = getSettledValue(areaTreeResult);

  return {
    flowMeterDeviceIds: toDeviceOptions(flowMeterRes?.list),
    gasSources: toGasSourceTreeOptions(areaTreeRes?.list ?? []),
    ...(currentScope === ScopeKey.Station ? { stationName: toAreaTreeOptions(areaTreeRes?.list ?? []) } : {}),
    ...(currentScope === ScopeKey.Area ? { areaName: toAreaTreeOptions(areaTreeRes?.list ?? []) } : {}),
    ...(currentScope === ScopeKey.Compressor ? { compressorName: toDeviceNameOptions(compressorRes?.list) } : {}),
  };
};
